import React, { useEffect, useState } from "react";
import {
  getSettings,
  updateSettings,
  updateDefaultImageLicence,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";

const PaiementSettingsForm = () => {
  const [settings, setSettings] = useState({
    max_payments: 3,
    payment_interval: 1,
    photo_url_default_lic_cot: "",
  });

  const [loading, setLoading] = useState(true);

  // Modale d’upload
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // On stocke tel quel, on convertira au submit
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Conversion des champs numériques avant envoi
    const payload = {
      ...settings,
      max_payments: Number(settings.max_payments) || 1,
      payment_interval: Number(settings.payment_interval) || 1,
    };
    try {
      await updateSettings(payload);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  // Upload image par défaut
  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
  };
  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateDefaultImageLicence(selectedFile, token);
      setSettings((prev) => ({
        ...prev,
        photo_url_default_lic_cot: imageUrl,
      }));
      toast.success("Image par défaut mise à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image :", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const imageSrc =
    settings.photo_url_default_lic_cot?.trim() ||
    "/img/article_placeholder.jpg";

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Paiements — Paramètres & Image par défaut
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Définissez le nombre d’échéances, l’intervalle et l’image par
                défaut du règlement.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire principal */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Échéancier de paiement
                </h3>

                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nombre maximum de paiements (Licence-Assurance & Cotisation)
                </label>
                <select
                  name="max_payments"
                  value={settings.max_payments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {[...Array(5)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>

                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Espacement des paiements (en mois)
                </label>
                <select
                  name="payment_interval"
                  value={settings.payment_interval}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {[1, 2, 3].map((interval) => (
                    <option key={interval} value={interval}>
                      {interval}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bouton bas du formulaire */}
              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  className="!rounded-xl gap-2"
                  icon={FaSave}
                />
              </div>
            </form>

            {/* Carte Image par défaut */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Image par défaut — Règlement d’inscription
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr,200px] gap-4 items-start">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    URL actuelle (lecture seule)
                  </label>
                  <input
                    type="text"
                    value={settings.photo_url_default_lic_cot || ""}
                    readOnly
                    className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/60 dark:bg-slate-900/60 px-3 py-2 text-slate-900 dark:text-slate-100
                               focus:outline-none select-all cursor-text"
                    onFocus={(e) => e.target.select()}
                    placeholder="— non défini —"
                  />

                  <div className="mt-3">
                    <Button
                      text="Changer l'image"
                      onClick={openUploadModal}
                      className="!rounded-xl gap-2"
                    />
                  </div>

                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                    Formats recommandés : JPG/PNG/WebP.
                  </p>
                </div>

                <div className="relative">
                  <img
                    src={imageSrc}
                    alt="Aperçu image par défaut"
                    className="w-full h-40 sm:h-48 object-cover rounded-xl shadow ring-2 ring-white dark:ring-slate-800"
                    onError={(e) => {
                      e.currentTarget.src = "/img/article_placeholder.jpg";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modale Upload */}
      {isUploadModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Changer l'image par défaut"
          onClick={(e) => e.currentTarget === e.target && closeUploadModal()}
        >
          <div className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95">
            {/* En-tête */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                Changer l’image par défaut
              </h3>
              <button
                onClick={closeUploadModal}
                className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
                aria-label="Fermer"
                type="button"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            {/* Corps */}
            <form
              onSubmit={handleUploadSubmit}
              className="p-4 sm:p-6 space-y-4"
            >
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Sélectionnez une image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {selectedFile && (
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Fichier sélectionné :{" "}
                  <span className="font-semibold">{selectedFile.name}</span>
                </p>
              )}

              <div className="mt-2 flex items-center justify-end gap-3">
                <Button
                  text="Annuler"
                  icon={FaTimes}
                  type="button"
                  onClick={closeUploadModal}
                  className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                />
                <Button
                  text={isUploading ? "Téléversement..." : "Sauvegarder"}
                  icon={FaSave}
                  type="submit"
                  className="!rounded-xl gap-2"
                  isLoading={isUploading}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Styles locaux */}
      <style>{`
        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Apple-friendly text smoothing */
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default PaiementSettingsForm;
